/* eslint-disable @typescript-eslint/no-explicit-any */
import {ChangeDetectorRef, DestroyRef, Injectable} from '@angular/core';
import {MetaData, NgEventBus} from 'ng-event-bus';
import {SpatialUtilService} from '../../../../services/spatial-util.service';
import {MapToolsService} from '../../../../services/map-tools.service';
import {ConstructionToolModelService} from './construction-tool-model.service';
import {EsriModuleService} from '../../../../esri-nlr/services/esri-module.service';
import {SpatialSequenceService} from '../../../../../common/service/spatial-sequence.service';
import {FeaturesApplyInsertService} from '../../../../services/features-apply-insert.service';
import {InterestService} from '../../../../services/features/interest.service';
import {InterestReconciliationService} from '../../../../../common/service/interest-reconciliation.service';
import {LotService} from '../../../../services/features/lot.service';
import {PlanModelService} from '../../../../../common/service/plan-model.service';
import {switchMap, tap} from 'rxjs/operators';
import {Subscription} from 'rxjs';
import {equals} from '../../../../../common/service/data-util';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {ToolServiceInterface} from './tool-service-interface';

@Injectable({
    providedIn: 'root'
})
export class LotConstructionToolService implements ToolServiceInterface {
    title = 'LotConstructionToolService';

    attributes: any[];

    private selectedPolygons = [];

    private minPolygonsError = 'At least 1 polygon must be selected to create a lot.';

    private featureHighlightsChangedSubscription: Subscription;

    constructor(
        private constructionToolModelService: ConstructionToolModelService,
        private esriModuleService: EsriModuleService,
        private eventBus: NgEventBus,
        private featuresApplyInsertService: FeaturesApplyInsertService,
        private interestService: InterestService,
        private interestReconciliationService: InterestReconciliationService,
        private lotService: LotService,
        private planModelService: PlanModelService,
        private mapToolsService: MapToolsService,
        private spatialSequenceService: SpatialSequenceService,
        private spatialUtilService: SpatialUtilService,
        private destroyRef: DestroyRef
    ) {}

    showAttribute() {
        const selectedPolygons = this.spatialUtilService.getHighlightedPolygons();
        return !!selectedPolygons.length;
    }

    setupFeatureEventModification(options: {changeDetectorRef: ChangeDetectorRef; eventBus: NgEventBus}) {
        console.debug(`${this.title} - setupFeatureEventModification`);

        this.attributes = this.constructionToolModelService.getToolAttrs('lot');

        this.onChangeSelectedPolygons();

        if (this.featureHighlightsChangedSubscription) {
            this.featureHighlightsChangedSubscription.unsubscribe();
        }

        this.featureHighlightsChangedSubscription = options.eventBus
            .on('featureHighlightsChanged')
            .pipe(
                takeUntilDestroyed(this.destroyRef),
                tap(() => {
                    console.debug('on featureHighlightChanged with lot construction tool');
                })
            )
            .subscribe((meta: MetaData) => {
                this.onChangeSelectedPolygons();

                const oldFeatureHighlightsMap = meta.data;
                const previouslyHighlightedPolygonIds = oldFeatureHighlightsMap.polygon || [];
                const highlightedPolygonIds = this.spatialUtilService.getHighlightedFeatureIds('polygon');
                if (!equals(highlightedPolygonIds, previouslyHighlightedPolygonIds)) {
                    this.validate();
                }
                options.changeDetectorRef.detectChanges();
            });

        if (this.spatialUtilService.getHighlightedPolygons().length === 0) {
            this.validate();
        }
    }

    create(attributes: any) {
        console.debug(`${this.title} - create`);
        return this.mapToolsService.activate('CONSTRUCTION_TOOLS_LOT', () => {
            const tool = this.getTool();

            tool.errors.length = 0;
            tool.warnings.length = 0;
            tool.information.length = 0;

            if (this.spatialUtilService.getHighlightedPolygons().length === 0) {
                tool.errors.push(this.minPolygonsError);
                return;
            }

            if (!this.isLotNameValid(attributes)) {
                tool.errors.push(`${tool.errorMessage1} : ${attributes.name}`);
            } else if (!this.isPolygonsTypeValid(attributes)) {
                tool.errors.push(tool.errorMessage3);
            } else if (!this.isSelectedPolygonsUsageValid()) {
                tool.errors.push(this.getPolygonsUsageErrorMessage());
            } else {
                const polygonGeometry = new this.esriModuleService.geometry.Polygon();
                polygonGeometry.setSpatialReference(this.selectedPolygons[0].geometry.spatialReference);

                this.selectedPolygons.forEach((polygon: any) => polygon.geometry.rings.forEach((ring: any) => polygonGeometry.addRing(ring)));

                let purpose = attributes.purpose;

                if (purpose && purpose.id.indexOf('[') === -1) {
                    purpose = {id: `[${purpose.id}]`};
                }

                const lotAttributes: any = {
                    type: attributes.type.id,
                    name: attributes.name,
                    totalarea: attributes.totalarea,
                    purpose: purpose ? purpose.id : undefined,
                    statutoryreference: attributes.statutoryreference,
                    comment: attributes.comment
                };

                if (attributes.acquisitiontransferaction) {
                    lotAttributes.acquisitiontransferaction = attributes.acquisitiontransferaction.id;
                }

                // road attributes
                if (attributes.roadid) {
                    const road = attributes.roadid;

                    lotAttributes.roadid = road.id;
                    lotAttributes.roadname = road.roadName;
                    lotAttributes.roadtype = road.roadType;
                    lotAttributes.roadsuffix = road.roadSuffix;
                }

                const lotGraphic = new this.esriModuleService.Graphic(polygonGeometry);
                lotGraphic.attributes = lotAttributes;

                lotGraphic.attributes.id = this.spatialSequenceService.getLotSequence(1)[0];

                const subscription = this.featuresApplyInsertService
                    .insertFeaturesByType(this.spatialUtilService.getLotMapLayer(), [lotGraphic])
                    .pipe(
                        switchMap((createdLot: any) => {
                            console.debug('Lot has been successfully created!');
                            return this.lotService.saveLotAssociation(createdLot[0].attributes.id);
                        }),
                        switchMap(() => {
                            // DCX-7971: update interest boundaries on lot creation
                            const nonSpatialInterests = this.interestReconciliationService.getNonSpatialInterests(
                                this.planModelService.getModel().formerTenures,
                                this.planModelService.getModel().interests
                            );
                            return this.interestService.updateInterestGeometry(nonSpatialInterests);
                        })
                    )
                    .subscribe();

                this.eventBus.cast('nlr:busy', subscription);
            }
        });
    }

    private populateTypeAttribute(attributes: any) {
        console.debug(`${this.title} - populateTypeAttribute`);

        Object.values(attributes).forEach((attribute: any) => {
            if (attribute.prop === 'type' && this.selectedPolygons.length > 0) {
                const polygonTypes = this.selectedPolygons.map((polygon: any) => polygon.attributes.type).filter((v, i, a) => i === a.indexOf(v));

                if (polygonTypes.length === 1) {
                    attribute.values.forEach((value: any) => {
                        if (value.id === polygonTypes[0] || value.description === polygonTypes[0]) {
                            attribute.value = value;
                        }
                    });
                } else {
                    attribute.value = undefined;
                }
            }
        });
    }

    private onChangeSelectedPolygons() {
        const polygons = this.spatialUtilService.getHighlightedPolygons();
        const polygonIds = polygons.map((p: any) => p.attributes.id);

        const totalAreaAttr = this.attributes.find((a: any) => a.prop === 'totalarea');
        const oldTotalArea = totalAreaAttr ? totalAreaAttr.value : 0;
        const newTotalArea = polygons.map((p: any) => p.attributes.statedarea).reduce((acc: any, v: any) => (v ? acc + Number(v) : acc), 0);

        if (
            this.selectedPolygons.length === polygonIds.length &&
            this.selectedPolygons.every(p => polygonIds.includes(p.attributes.id)) &&
            newTotalArea === oldTotalArea
        ) {
            return;
        }

        this.selectedPolygons = polygons;
        if (totalAreaAttr) {
            totalAreaAttr.value = newTotalArea;
        }

        this.populateTypeAttribute(this.attributes);
    }

    private getTool() {
        return this.constructionToolModelService.getTool('lot');
    }

    private validate() {
        console.debug(`${this.title} - validate`);

        const tool = this.getTool();

        const minPolygonsErrorIdx = tool.errors.indexOf(this.minPolygonsError);
        if (this.spatialUtilService.getHighlightedPolygons().length) {
            if (minPolygonsErrorIdx !== -1) {
                tool.errors.splice(minPolygonsErrorIdx, 1);
            }
        } else if (minPolygonsErrorIdx === -1) {
            tool.errors.length = 0;
            tool.errors.push(this.minPolygonsError);
        }
    }

    private isLotNameValid(attributes: any) {
        console.debug(`${this.title} - isLotNameValid`);
        return this.spatialUtilService.getLots().every((lot: any) => String(attributes.name) !== String(lot.attributes.name));
    }

    private isPolygonsTypeValid(attributes: any) {
        console.debug(`${this.title} - isPolygonsTypeValid`);
        return this.selectedPolygons.every((p: any) => p.attributes.type === attributes.type.id || p.attributes.type === attributes.type.description);
    }

    private isSelectedPolygonsUsageValid() {
        console.debug(`${this.title} - isSelectedPolygonsUsageValid`);

        return this.spatialUtilService
            .getLots()
            .filter((lot: any) => lot.attributes.polygonids)
            .every((lot: any) => this.selectedPolygons.every(polygon => !lot.attributes.polygonids.includes(`[${polygon.attributes.id}]`)));
    }

    private getPolygonsUsageErrorMessage() {
        console.debug(`${this.title} - getPolygonsUsageErrorMessage`);

        const polygonsInUse = [];

        this.spatialUtilService.getLots().forEach((lot: any) =>
            this.selectedPolygons.forEach((polygon: any) => {
                if (lot.attributes.polygonids && lot.attributes.polygonids.includes(`[${polygon.attributes.id}]`)) {
                    polygonsInUse.push({lotName: lot.attributes.name, polygonId: polygon.attributes.id});
                }
            })
        );

        let message = '';

        if (polygonsInUse.length > 0) {
            const tool = this.getTool();
            polygonsInUse.forEach((polygonInUse: any) => {
                message += tool.errorMessage2.replace('[POLYGON_ID]', polygonInUse.polygonId).replace('[LOT_NAME]', polygonInUse.lotName);
            });
        }

        return message;
    }
}
